import { WbaEvents } from "./wba-events";
import type { TWbaEventCall, TWbaFunction } from "./wba-types";

type TWbaGlobal = typeof globalThis & { wba?: TWbaFunction };

type TQueuedCall = {
  call: TWbaEventCall;
  oneTime: boolean;
};

const sentOnce = new Set<string>();
const queue: TQueuedCall[] = [];
const WARN_AFTER_ATTEMPTS = 30;

let isWaiting = false;
let isInited = false;

const getWbaFn = (): TWbaFunction | undefined => {
  const wbaRaw = (globalThis as TWbaGlobal).wba;

  return typeof wbaRaw === "function" ? wbaRaw : undefined;
};

const sendToWba = (call: TWbaEventCall, oneTime: boolean) => {
  const key = `event:${call[0]}`;

  if (oneTime && sentOnce.has(key)) {
    return;
  }

  if (oneTime) {
    sentOnce.add(key);
  }

  getWbaFn()?.(...call);
};

export const flushWbaQueue = () => {
  if (queue.length === 0) {
    return;
  }

  const wbaFn = getWbaFn();

  if (!wbaFn || !isInited) {
    return;
  }

  const initIndex = queue.findIndex((item) => item.call[0] === WbaEvents.INIT);

  if (initIndex !== -1) {
    const initItem = queue.splice(initIndex, 1)[0];

    if (initItem) {
      sendToWba(initItem.call, initItem.oneTime);
    }
  }

  while (queue.length > 0) {
    const item = queue.shift();

    if (item) {
      sendToWba(item.call, item.oneTime);
    }
  }
};

export const wbaSendEvent = (call: TWbaEventCall, oneTime = false) => {
  const key = `event:${call[0]}`;

  if (oneTime && sentOnce.has(key)) {
    return;
  }

  if (call[0] === WbaEvents.INIT) {
    isInited = true;
  }

  const wbaFn = getWbaFn();

  if (wbaFn && isInited) {
    sendToWba(call, oneTime);

    return;
  }

  queue.push({ call, oneTime });

  if (isWaiting) {
    return;
  }

  isWaiting = true;
  let attempts = 0;

  const interval = setInterval(() => {
    attempts += 1;

    if (getWbaFn() && isInited) {
      clearInterval(interval);
      flushWbaQueue();
      isWaiting = false;

      return;
    }

    if (attempts === WARN_AFTER_ATTEMPTS && queue.length > 0) {
      console.warn(
        `WBA: still waiting for SDK, ${queue.length} event(s) in queue`,
        queue.map((item) => item.call[0]),
      );
    }
  }, 1000);
};
